import { db } from '@/db/database'
import type { Attachment, AttachmentInput, Entry, EntryData, EntryType, JournalData, MealData } from '@/types'
import { createId } from '@/utils/id'
import { localDateTimeToIso, toLocalDateString } from '@/utils/date'

export interface EntryFilters {
  type?: EntryType
  date?: string
  month?: string
  keyword?: string
  mood?: string
  category?: string
}

export interface EntryDraft {
  id?: string
  type: EntryType
  occurredOn: string
  time?: string
  data: EntryData
}

function searchable(entry: Entry): string {
  return JSON.stringify(entry.data).toLocaleLowerCase('zh-CN')
}

export const entryRepository = {
  async list(filters: EntryFilters = {}): Promise<Entry[]> {
    let items = (await db.entries.toArray()).filter((entry) => !entry.deletedAt)
    if (filters.type) items = items.filter((entry) => entry.type === filters.type)
    if (filters.date) items = items.filter((entry) => entry.occurredOn === filters.date)
    if (filters.month) items = items.filter((entry) => entry.occurredOn.startsWith(filters.month!))
    if (filters.keyword) {
      const keyword = filters.keyword.toLocaleLowerCase('zh-CN')
      items = items.filter((entry) => searchable(entry).includes(keyword))
    }
    if (filters.mood) items = items.filter((entry) => (entry.data as JournalData).mood === filters.mood)
    if (filters.category) items = items.filter((entry) => (entry.data as { category?: string }).category === filters.category)
    return items.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
  },

  async get(id: string): Promise<Entry | undefined> {
    const item = await db.entries.get(id)
    return item?.deletedAt ? undefined : item
  },

  async save(draft: EntryDraft, additions: AttachmentInput[] = [], removedAttachmentIds: string[] = []): Promise<Entry> {
    const existing = draft.id ? await db.entries.get(draft.id) : undefined
    const id = existing?.id || createId()
    const now = new Date().toISOString()
    const attachmentIds = [
      ...(((draft.data as JournalData | MealData).attachmentIds || []).filter((item) => !removedAttachmentIds.includes(item))),
      ...additions.map((item) => item.id)
    ]
    const data = draft.type === 'journal' || draft.type === 'meal'
      ? { ...draft.data, attachmentIds }
      : draft.data
    const entry: Entry = {
      id,
      type: draft.type,
      occurredOn: draft.occurredOn || toLocalDateString(),
      occurredAt: localDateTimeToIso(draft.occurredOn, draft.time || '12:00'),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
      deletedAt: null,
      data
    }
    const attachments: Attachment[] = additions.map((item) => ({ ...item, entryId: id, createdAt: now }))
    await db.transaction('rw', db.entries, db.attachments, async () => {
      await db.entries.put(entry)
      if (attachments.length) await db.attachments.bulkPut(attachments)
      if (removedAttachmentIds.length) await db.attachments.bulkDelete(removedAttachmentIds)
    })
    return entry
  },

  async softDelete(id: string): Promise<void> {
    const entry = await db.entries.get(id)
    if (!entry) return
    const now = new Date().toISOString()
    await db.entries.update(id, { deletedAt: now, updatedAt: now })
  }
}
